import { useSearchParams } from "react-router";
import {
  archiveSkillAction,
  deleteSkillAction,
  restoreSkillAction,
} from "@/actions/skills";
import { useApiQuery } from "@/api/useApiQuery";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { PlusIcon } from "@/components/Icon";
import { LinkButton } from "@/components/LinkButton";
import { ListingSummary } from "@/components/ListingSummary";
import { PageHeader } from "@/components/PageHeader";
import { RowActions } from "@/components/RowActions";
import { StatusBadge } from "@/components/StatusBadge";
import type { SkillListRow } from "@shared/dto";
import { formatUsage } from "@shared/usage";
import { SkillDraftRow } from "./skills/SkillDraftRow";
import { SkillEditRow } from "./skills/SkillEditRow";

const SKILL_STATUS_ACTIVE = "ACTIVE";

const dateFormatter = new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" });

const columns: DataTableColumn<SkillListRow>[] = [
  {
    key: "code",
    label: "Code",
    info: "Stable machine identifier, UPPER_SNAKE (e.g. READING). Achievements and unlocks reference it by name, so it cannot be renamed after use.",
    render: (row) => row.code,
  },
  {
    key: "label",
    label: "Label",
    info: "Human-readable name shown to users, in the default locale (en).",
    render: (row) => row.label ?? "—",
  },
  {
    key: "description",
    label: "Description",
    info: "What the skill covers, in the default locale — e.g. 'Time spent with books and long-form text'.",
    isProse: true,
    render: (row) => row.description ?? "—",
  },
  {
    key: "icon",
    label: "Icon",
    info: "Icon name used for the skill in the app. Free text for now.",
    render: (row) => row.icon ?? "—",
  },
  {
    key: "color",
    label: "Color",
    info: "Hex color used for the skill badge in the app, e.g. #B68235.",
    render: (row) => row.color ?? "—",
  },
  {
    key: "status",
    label: "Status",
    render: (row) => (
      <StatusBadge
        label={row.status}
        tone={row.status === SKILL_STATUS_ACTIVE ? "accent" : "neutral"}
      />
    ),
  },
  {
    key: "createdAt",
    label: "Created",
    // JSON has no date type, so what arrives over the wire is the string the
    // server serialised. The listing formats it rather than the query.
    render: (row) => dateFormatter.format(new Date(row.createdAt)),
  },
];

export const SkillsPage = () => {
  const [searchParams] = useSearchParams();
  const { data, error, isLoading, refresh } =
    useApiQuery<SkillListRow[]>("/skills");

  const skills = data ?? [];
  const isDraftOpen = searchParams.get("new") === "1";
  const archivedCount = skills.filter(
    (row) => row.status !== SKILL_STATUS_ACTIVE,
  ).length;
  // A half-typed new skill outranks an edit link: the draft would be thrown
  // away by rendering the edit row instead.
  const editingKey = isDraftOpen
    ? undefined
    : (searchParams.get("edit") ?? undefined);

  return (
    <>
      <PageHeader
        kicker="Content"
        title="Skills"
        action={
          isDraftOpen ? null : (
            <LinkButton href="/skills?new=1" variant="primary">
              <PlusIcon size={14} />
              New skill
            </LinkButton>
          )
        }
      />
      <ListingSummary
        counts={[
          { label: "skill", value: skills.length },
          { label: "archived", value: archivedCount, shouldPluralize: false },
        ]}
      />
      <DataTable
        label="Skills"
        columns={columns}
        rows={skills}
        getRowKey={(row) => String(row.id)}
        emptyMessage={
          error ?? (isLoading ? "Loading..." : "No skills yet.")
        }
        draftRow={isDraftOpen ? <SkillDraftRow onSaved={refresh} /> : undefined}
        editingKey={editingKey}
        renderEditRow={(row) => (
          <SkillEditRow
            id={row.id}
            code={row.code}
            label={row.label}
            description={row.description}
            icon={row.icon}
            color={row.color}
            onSaved={refresh}
          />
        )}
        rowActions={(row) => (
          <RowActions
            editHref={`/skills?edit=${row.id}`}
            isInactive={row.status !== SKILL_STATUS_ACTIVE}
            retireLabel="Archive"
            retirePendingLabel="Archiving..."
            confirmTitle={`Archive ${row.code}?`}
            confirmBody={
              <>
                <p>{formatUsage(row.templateCount, "quest template")}</p>
                <p>
                  The skill stays in the database and keeps appearing wherever
                  it is already used, marked as archived, and its code stays
                  taken. You can restore it from this listing.
                </p>
              </>
            }
            retireAction={archiveSkillAction.bind(null, row.id)}
            restoreAction={restoreSkillAction.bind(null, row.id)}
            onCompleted={refresh}
            deleteConfirmTitle={`Delete ${row.code}?`}
            deleteConfirmBody={
              <>
                <p>{formatUsage(row.templateCount, "quest template")}</p>
                <p>
                  The skill and its translations are removed for good and the
                  code {row.code} becomes available again. This cannot be
                  undone, and it is refused while any quest template still binds
                  the skill.
                </p>
              </>
            }
            // Archive first: archiving is what stops new templates binding
            // the skill, so it drains the room before we check it is empty.
            deleteAction={
              row.status === SKILL_STATUS_ACTIVE
                ? undefined
                : deleteSkillAction.bind(null, row.id)
            }
          />
        )}
      />
    </>
  );
};
